import { auth, storage } from "@/lib/firebase";
import { File as ExpoFile } from "expo-file-system";
import * as ImagePicker from "expo-image-picker";
import { Platform } from "react-native";
import {
  getDownloadURL,
  ref,
  uploadBytesResumable,
  type UploadTaskSnapshot,
} from "firebase/storage";

export type ImageUploadPhase =
  | "preparing"
  | "optimizing"
  | "uploading"
  | "retrying"
  | "complete";

export type ImageUploadProgress = {
  phase: ImageUploadPhase;
  message: string;
  percent?: number;
  attempt?: number;
};

export type ImageOptimizationStep = {
  longestSide: number;
  quality: number;
};

export type OptimizedImageUploadResult = {
  downloadURL: string;
  storagePath: string;
  sizeBytes: number;
  width: number | null;
  height: number | null;
  contentType: "image/jpeg";
};

type UploadImageData = Blob | Uint8Array | ArrayBuffer;

type PreparedUploadData = {
  data: UploadImageData;
  sizeBytes: number;
};

type OptimizedImageResult = {
  uri: string;
  width: number | null;
  height: number | null;
  sizeBytes: number;
};

export type PickOptimizeAndUploadImageParams = {
  storagePath: string;
  optimizationSteps?: ImageOptimizationStep[];
  preferredMaxBytes?: number;
  maxBytes?: number;
  maxRetries?: number;
  onProgress?: (progress: ImageUploadProgress) => void;
};

export const IMAGE_UPLOAD_CONTENT_TYPE = "image/jpeg" as const;
export const MAX_IMAGE_UPLOAD_BYTES = 5 * 1024 * 1024;
export const DEFAULT_IMAGE_UPLOAD_TARGET_BYTES = 1.5 * 1024 * 1024;
export const IMAGE_PICKER_CANCELLED_MESSAGE = "cancelled";
export const IMAGE_TOO_LARGE_MESSAGE =
  "This image is still too large after optimization. Please choose a smaller image or crop it first.";
export const UNSUPPORTED_IMAGE_TYPE_MESSAGE =
  "Please choose a JPG, PNG, HEIC, or WebP image.";

const DEFAULT_MAX_RETRIES = 3;

const DEFAULT_OPTIMIZATION_STEPS: ImageOptimizationStep[] = [
  { longestSide: 1600, quality: 0.82 },
  { longestSide: 1400, quality: 0.75 },
  { longestSide: 1200, quality: 0.68 },
  { longestSide: 1000, quality: 0.6 },
  { longestSide: 900, quality: 0.55 },
  { longestSide: 800, quality: 0.5 },
  { longestSide: 700, quality: 0.45 },
];

const SUPPORTED_IMAGE_MIME_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
]);

const SUPPORTED_IMAGE_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "png",
  "webp",
  "heic",
  "heif",
]);

function devLog(message: string, data?: Record<string, unknown>) {
  if (!__DEV__) return;

  void data;
  console.log(`[image upload] ${message}`);
}

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isNativeLocalUri(uri: string) {
  return (
    Platform.OS !== "web" &&
    /^(file|content|asset-library):/i.test(String(uri || ""))
  );
}

function getUriExtension(uri: string) {
  const clean = String(uri || "")
    .split("?")[0]
    .split("#")[0];
  const ext = clean.split(".").pop()?.toLowerCase() ?? "";

  return ext.length > 0 && ext.length <= 5 ? ext : "";
}

function getAssetExtension(asset: ImagePicker.ImagePickerAsset) {
  const fileNameExt = getUriExtension(asset.fileName ?? "");

  if (fileNameExt) return fileNameExt;

  return getUriExtension(asset.uri);
}

function toPositiveSizeBytes(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : null;
}

function formatMegabytes(sizeBytes: number | null) {
  if (sizeBytes === null) return "unknown";

  return `${(sizeBytes / (1024 * 1024)).toFixed(2)}MB`;
}

function isBlobUploadData(data: UploadImageData): data is Blob {
  return typeof Blob !== "undefined" && data instanceof Blob;
}

function getUploadDataSizeBytes(data: UploadImageData) {
  if (isBlobUploadData(data)) {
    return data.size;
  }

  return data.byteLength;
}

function getResizeAction(
  asset: ImagePicker.ImagePickerAsset,
  longestSide: number
) {
  const width = Number.isFinite(asset.width) ? asset.width : 0;
  const height = Number.isFinite(asset.height) ? asset.height : 0;
  const originalLongestSide = Math.max(width, height);
  const targetSide =
    originalLongestSide > 0
      ? Math.min(longestSide, originalLongestSide)
      : longestSide;

  if (height > width) {
    return { resize: { height: targetSide } };
  }

  return { resize: { width: targetSide } };
}

function isNonRetryableUploadError(error: unknown) {
  const code = String((error as { code?: unknown })?.code ?? "");

  return [
    "storage/unauthenticated",
    "storage/unauthorized",
    "storage/canceled",
    "storage/invalid-argument",
    "storage/invalid-checksum",
    "storage/quota-exceeded",
    "storage/invalid-format",
  ].includes(code);
}

function emitProgress(
  onProgress: PickOptimizeAndUploadImageParams["onProgress"],
  progress: ImageUploadProgress
) {
  onProgress?.(progress);
}

async function ensureLibraryPermission() {
  const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();

  if (!permission.granted) {
    throw new Error("Allow photo access to upload images.");
  }
}

async function getImageManipulator() {
  try {
    return await import("expo-image-manipulator");
  } catch {
    throw new Error(
      "Image tools are not available yet. Install expo-image-manipulator and rebuild the app."
    );
  }
}

async function pickSingleImage() {
  await ensureLibraryPermission();

  const result = await ImagePicker.launchImageLibraryAsync({
    mediaTypes: ["images"],
    allowsEditing: false,
    quality: 1,
    exif: false,
  });

  if (result.canceled || !result.assets?.[0]?.uri) {
    throw new Error(IMAGE_PICKER_CANCELLED_MESSAGE);
  }

  const asset = result.assets[0];
  validateSelectedImageType(asset);

  return asset;
}

function validateSelectedImageType(asset: ImagePicker.ImagePickerAsset) {
  const mimeType = asset.mimeType?.toLowerCase() ?? "";
  const extension = getAssetExtension(asset);

  if (mimeType) {
    if (!mimeType.startsWith("image/")) {
      throw new Error(UNSUPPORTED_IMAGE_TYPE_MESSAGE);
    }

    if (!SUPPORTED_IMAGE_MIME_TYPES.has(mimeType)) {
      throw new Error(UNSUPPORTED_IMAGE_TYPE_MESSAGE);
    }

    return;
  }

  if (extension && !SUPPORTED_IMAGE_EXTENSIONS.has(extension)) {
    throw new Error(UNSUPPORTED_IMAGE_TYPE_MESSAGE);
  }

  devLog("selected image type could not be fully verified", {
    fileName: asset.fileName ?? null,
    extension: extension || null,
  });
}

async function readUploadData(uri: string): Promise<PreparedUploadData> {
  if (isNativeLocalUri(uri)) {
    try {
      const file = new ExpoFile(uri);

      if (!file.exists) {
        throw new Error("Selected image file does not exist.");
      }

      const data = await file.bytes();
      const sizeBytes = getUploadDataSizeBytes(data);

      if (!sizeBytes) {
        throw new Error("Selected image file is empty.");
      }

      return { data, sizeBytes };
    } catch (error) {
      devLog("native file read failed", {
        uriPrefix: String(uri || "").slice(0, 30),
        error,
      });

      throw new Error("Could not read the selected image. Please try again.");
    }
  }

  try {
    const response = await fetch(uri);

    if (!response.ok) {
      throw new Error(`Could not read file. Status: ${response.status}`);
    }

    const data = await response.blob();
    const sizeBytes = getUploadDataSizeBytes(data);

    if (!sizeBytes) {
      throw new Error("Selected image file is empty.");
    }

    return { data, sizeBytes };
  } catch (error) {
    devLog("uri/blob read failed", {
      uriPrefix: String(uri || "").slice(0, 30),
      error,
    });

    throw new Error("Could not read the selected image. Please try again.");
  }
}

async function optimizeImageForUpload(params: {
  asset: ImagePicker.ImagePickerAsset;
  steps: ImageOptimizationStep[];
  preferredMaxBytes: number;
  maxBytes: number;
}) {
  const ImageManipulator = await getImageManipulator();
  let bestUnderMax: OptimizedImageResult | null = null;
  const originalSizeBytes =
    toPositiveSizeBytes(params.asset.fileSize) ??
    (await readSizeForLog(params.asset.uri));

  devLog("selected image", {
    size: formatMegabytes(originalSizeBytes),
    sizeBytes: originalSizeBytes,
    width: params.asset.width ?? null,
    height: params.asset.height ?? null,
    mimeType: params.asset.mimeType ?? null,
    fileName: params.asset.fileName ?? null,
  });

  for (const step of params.steps) {
    const action = getResizeAction(params.asset, step.longestSide);

    devLog("optimization step started", {
      longestSide: step.longestSide,
      quality: step.quality,
      resize: action.resize,
    });

    const processed = await ImageManipulator.manipulateAsync(
      params.asset.uri,
      [action],
      {
        compress: step.quality,
        format: ImageManipulator.SaveFormat.JPEG,
      }
    );
    const { sizeBytes } = await readUploadData(processed.uri);

    devLog("optimization step finished", {
      longestSide: step.longestSide,
      quality: step.quality,
      size: formatMegabytes(sizeBytes),
      sizeBytes,
      width: processed.width ?? null,
      height: processed.height ?? null,
    });

    const candidate: OptimizedImageResult = {
      uri: processed.uri,
      width: processed.width ?? params.asset.width ?? null,
      height: processed.height ?? params.asset.height ?? null,
      sizeBytes,
    };

    if (sizeBytes < params.maxBytes) {
      bestUnderMax = candidate;

      if (sizeBytes <= params.preferredMaxBytes) {
        return candidate;
      }
    }
  }

  if (bestUnderMax) {
    return bestUnderMax;
  }

  throw new Error(IMAGE_TOO_LARGE_MESSAGE);
}

async function readSizeForLog(uri: string) {
  try {
    return (await readUploadData(uri)).sizeBytes;
  } catch {
    return null;
  }
}

async function uploadDataResumable(params: {
  data: UploadImageData;
  storagePath: string;
  contentType: "image/jpeg";
  attempt: number;
  onProgress?: (progress: ImageUploadProgress) => void;
}) {
  const storageRef = ref(storage, params.storagePath);

  await new Promise<void>((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, params.data, {
      contentType: params.contentType,
    });

    task.on(
      "state_changed",
      (snapshot: UploadTaskSnapshot) => {
        const percent =
          snapshot.totalBytes > 0
            ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
            : undefined;

        emitProgress(params.onProgress, {
          phase: "uploading",
          percent,
          attempt: params.attempt,
          message:
            typeof percent === "number"
              ? `Uploading image... ${percent}%`
              : "Uploading image...",
        });
      },
      reject,
      resolve
    );
  });

  return await getDownloadURL(storageRef);
}

async function uploadDataResumableWithRetry(params: {
  data: UploadImageData;
  storagePath: string;
  contentType: "image/jpeg";
  maxRetries: number;
  onProgress?: (progress: ImageUploadProgress) => void;
}) {
  let lastError: unknown = null;

  for (let attemptIndex = 0; attemptIndex <= params.maxRetries; attemptIndex += 1) {
    const attempt = attemptIndex + 1;

    try {
      emitProgress(params.onProgress, {
        phase: "uploading",
        percent: 0,
        attempt,
        message:
          attempt > 1
            ? `Retrying upload... attempt ${attempt}`
            : "Uploading image... 0%",
      });

      const downloadURL = await uploadDataResumable({
        data: params.data,
        storagePath: params.storagePath,
        contentType: params.contentType,
        attempt,
        onProgress: params.onProgress,
      });

      return downloadURL;
    } catch (error) {
      lastError = error;

      devLog("upload attempt failed", {
        storagePath: params.storagePath,
        attempt,
        code: (error as { code?: unknown })?.code ?? null,
        message: (error as { message?: unknown })?.message ?? null,
      });

      if (
        attemptIndex >= params.maxRetries ||
        isNonRetryableUploadError(error)
      ) {
        break;
      }

      emitProgress(params.onProgress, {
        phase: "retrying",
        attempt: attempt + 1,
        message: "Upload paused. Retrying...",
      });

      await delay(500 + attemptIndex * 500);
    }
  }

  throw lastError;
}

export async function pickOptimizeAndUploadImage(
  params: PickOptimizeAndUploadImageParams
): Promise<OptimizedImageUploadResult> {
  const user = auth.currentUser;

  if (!user) {
    throw new Error("You must be signed in to upload images.");
  }

  const maxBytes = params.maxBytes ?? MAX_IMAGE_UPLOAD_BYTES;
  const preferredMaxBytes =
    params.preferredMaxBytes ?? DEFAULT_IMAGE_UPLOAD_TARGET_BYTES;
  const steps = params.optimizationSteps ?? DEFAULT_OPTIMIZATION_STEPS;
  const maxRetries = params.maxRetries ?? DEFAULT_MAX_RETRIES;

  emitProgress(params.onProgress, {
    phase: "preparing",
    message: "Preparing image...",
  });

  const asset = await pickSingleImage();

  emitProgress(params.onProgress, {
    phase: "optimizing",
    message: "Optimizing image...",
  });

  const optimized = await optimizeImageForUpload({
    asset,
    steps,
    preferredMaxBytes,
    maxBytes,
  });

  const { data, sizeBytes } = await readUploadData(optimized.uri);

  if (sizeBytes >= maxBytes) {
    devLog("optimized image rejected by hard max", {
      size: formatMegabytes(sizeBytes),
      sizeBytes,
      maxBytes,
    });

    throw new Error(IMAGE_TOO_LARGE_MESSAGE);
  }

  devLog("uploading optimized image", {
    authUid: user.uid,
    storagePath: params.storagePath,
    size: formatMegabytes(sizeBytes),
    sizeBytes,
    width: optimized.width,
    height: optimized.height,
    contentType: IMAGE_UPLOAD_CONTENT_TYPE,
  });

  const downloadURL = await uploadDataResumableWithRetry({
    data,
    storagePath: params.storagePath,
    contentType: IMAGE_UPLOAD_CONTENT_TYPE,
    maxRetries,
    onProgress: params.onProgress,
  });

  emitProgress(params.onProgress, {
    phase: "complete",
    percent: 100,
    message: "Image uploaded successfully.",
  });

  return {
    downloadURL,
    storagePath: params.storagePath,
    sizeBytes,
    width: optimized.width,
    height: optimized.height,
    contentType: IMAGE_UPLOAD_CONTENT_TYPE,
  };
}

export function isImageUploadCancelled(error: unknown) {
  return (
    String((error as { message?: unknown })?.message ?? "").toLowerCase() ===
    IMAGE_PICKER_CANCELLED_MESSAGE
  );
}

export function getFriendlyImageUploadError(error: unknown) {
  const message = String((error as { message?: unknown })?.message ?? "").trim();
  const code = String((error as { code?: unknown })?.code ?? "");

  if (!message) {
    return "Upload failed. Please check your connection and try again.";
  }

  if (
    message === IMAGE_TOO_LARGE_MESSAGE ||
    message === UNSUPPORTED_IMAGE_TYPE_MESSAGE ||
    message.includes("photo access") ||
    message.includes("signed in") ||
    message.includes("read the selected image")
  ) {
    return message;
  }

  if (code === "storage/unauthorized" || code === "storage/unauthenticated") {
    return "You do not have permission to upload this image. Please sign in again and check that this business is editable.";
  }

  if (
    code === "storage/retry-limit-exceeded" ||
    code === "storage/unknown" ||
    message.toLowerCase().includes("network")
  ) {
    return "Upload failed because the connection was interrupted. Please try again.";
  }

  return "Upload failed. Please try again with a JPG or PNG image.";
}
